import { FilterCar, filterPrice } from "utils/fleetFilter";
import { isNotBookable } from "utils/carPrice";

/**
 * 1NOW-11175: how the Vehicles page orders the cars it already filtered. "Recommended" is the
 * order the fleet arrived in. Price sorts use the price the card shows (the same one the price
 * filter compares); a car with no price to show (on request, or none yet) goes last in either
 * direction. A sort never drops or adds a car.
 */
export type FleetSort = "recommended" | "price-asc" | "price-desc" | "name";

export const FLEET_SORTS: { value: FleetSort; label: string }[] = [
  { value: "recommended", label: "Recommended" },
  { value: "price-asc", label: "Price: low to high" },
  { value: "price-desc", label: "Price: high to low" },
  { value: "name", label: "Name A to Z" },
];

// 1NOW-11193 (8 Oct): Design A's grids keep the sort in the URL in Design B's words
// (?sort=price-asc). A's "Z to A" has no twin in B and is written as name-desc.
export const DESIGN_A_SORT_URL: Record<string, string> = {
  aToZ: "name",
  zToA: "name-desc",
  priceLowToHigh: "price-asc",
  priceHighToLow: "price-desc",
};
export const designASortFromUrl = (word: string | null) =>
  Object.keys(DESIGN_A_SORT_URL).find((k) => DESIGN_A_SORT_URL[k] === word) ?? "default";

type SortCar = FilterCar & { name?: string | null };

const priceOf = (car: SortCar): number | null => (isNotBookable(car) ? null : filterPrice(car));

export const sortFleet = <T extends SortCar>(cars: T[] | null | undefined, sort: FleetSort): T[] => {
  const list = [...(cars ?? [])];
  if (sort === "recommended") return list;
  if (sort === "name") {
    return list.sort((a, b) => String(a.name ?? "").localeCompare(String(b.name ?? ""), undefined, { sensitivity: "base" }));
  }
  const dir = sort === "price-asc" ? 1 : -1;
  return list.sort((a, b) => {
    const pa = priceOf(a);
    const pb = priceOf(b);
    if (pa === null && pb === null) return 0;
    if (pa === null) return 1;
    if (pb === null) return -1;
    return (pa - pb) * dir;
  });
};
